// store/campaigns/actions.ts

import {
  CampaignActionTypes,
  CampaignSummaryDto,
  CampaignDetailDto,
  Pagination,
  Recipient,
  CampaignReportDto,
} from "./types";
import { ThunkAction } from "redux-thunk";
import { Action } from "redux";
import { ApplicationState } from "../index";
import config from "../../config";

const { api } = config;
const BASE = api.DTS_API_CAMPAIGN; // e.g. "https://api.zilter.io"

const makeHeaders = (token: string | null | undefined) => ({
  "Content-Type": "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export type AppThunk = ThunkAction<void, ApplicationState, null, Action>;

/** ─── LIST ────────────────────────────────────────────────────── */
interface FetchParams {
  status?: string;
  search?: string;
  created_start?: string;
  created_end?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  page?: number;
  size?: number;
  sort_by?: string;
  sort_order?: string;
}

export const campaignsGetRequest = ({
  status = "",
  search = "",
  created_start,
  created_end,
  scheduled_start,
  scheduled_end,
  page = 0,
  size = 20,
  sort_by = "created_at",
  sort_order = "desc",
}: FetchParams): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: CampaignActionTypes.GET_CAMPAIGNS_REQUEST });

  const params = new URLSearchParams();
  if (status)    params.append("status", status);
  if (search)    params.append("search", search);
  if (created_start) params.append("created_start", created_start);
  if (created_end) params.append("created_end", created_end);
  if (scheduled_start) params.append("scheduled_start", scheduled_start);
  if (scheduled_end) params.append("scheduled_end", scheduled_end);
  params.append("page", String(page));
  params.append("size", String(size));
  params.append("sort_by", sort_by);
  params.append("sort_order", sort_order);

  try {
    const res  = await fetch(`${BASE}/api/v1/campaigns?${params}`, { headers: makeHeaders(token) });
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || res.statusText);

    const {
      content,
      page: pg,
      size: sz,
      total_pages,
      total_elements,
      first,
      last,
    } = body as {
      content: CampaignSummaryDto[];
      page: number;
      size: number;
      total_pages: number;
      total_elements: number;
      first: boolean;
      last: boolean;
    };

    const pagination: Pagination = {
      page: pg,
      size: sz,
      pages: total_pages,
      elements: total_elements,
      first,
      last
    };

    dispatch({
      type: CampaignActionTypes.GET_CAMPAIGNS_SUCCESS,
      payload: { content, pagination },
    });
  } catch (err: any) {
    dispatch({
      type: CampaignActionTypes.GET_CAMPAIGNS_ERROR,
      payload: err.message || "Unknown error",
    });
  }
};

/** ─── GET SINGLE ───────────────────────────────────────────────── */
export const campaignGetRequest = (id: string): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: CampaignActionTypes.GET_CAMPAIGN_REQUEST });

  try {
    const res  = await fetch(`${BASE}/api/v1/campaigns/${id}`, { headers: makeHeaders(token) });
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || res.statusText);

    dispatch({
      type: CampaignActionTypes.GET_CAMPAIGN_SUCCESS,
      payload: body as CampaignDetailDto,
    });
  } catch (err: any) {
    dispatch({
      type: CampaignActionTypes.GET_CAMPAIGN_ERROR,
      payload: err.message || "Unknown error",
    });
  }
};

/** ─── CREATE ───────────────────────────────────────────────────── */
export const campaignCreateRequest = (data: Partial<CampaignDetailDto>): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: CampaignActionTypes.CREATE_CAMPAIGN_REQUEST });

  try {
    const res  = await fetch(
      `${BASE}/api/v1/campaigns`,
      { method: "POST", headers: makeHeaders(token), body: JSON.stringify(data) }
    );
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || res.statusText);

    dispatch({
      type: CampaignActionTypes.CREATE_CAMPAIGN_SUCCESS,
      payload: body, // assume { id: string } or the full detail
    });
  } catch (err: any) {
    dispatch({
      type: CampaignActionTypes.CREATE_CAMPAIGN_ERROR,
      payload: err.message || "Unknown error",
    });
  }
};

/** ─── UPDATE ───────────────────────────────────────────────────── */
export const campaignUpdateRequest = (id: string, data: Partial<CampaignDetailDto>): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: CampaignActionTypes.UPDATE_CAMPAIGN_REQUEST });

  try {
    const res  = await fetch(
      `${BASE}/api/v1/campaigns/${id}`,
      { method: "PUT", headers: makeHeaders(token), body: JSON.stringify(data) }
    );
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || res.statusText);

    dispatch({
      type: CampaignActionTypes.UPDATE_CAMPAIGN_SUCCESS,
      payload: body as CampaignDetailDto,
    });
  } catch (err: any) {
    dispatch({
      type: CampaignActionTypes.UPDATE_CAMPAIGN_ERROR,
      payload: err.message || "Unknown error",
    });
  }
};

/** ─── DELETE ───────────────────────────────────────────────────── */
export const campaignDeleteRequest = (id: string): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: CampaignActionTypes.DELETE_CAMPAIGN_REQUEST });

  try {
    const res = await fetch(`${BASE}/api/v1/campaigns/${id}`, {
      method: "DELETE",
      headers: makeHeaders(token)
    });
    if (!res.ok) {
      const body = await res.json();
      throw new Error(body.message || res.statusText);
    }

    dispatch({
      type: CampaignActionTypes.DELETE_CAMPAIGN_SUCCESS,
      payload: { id },
    });
  } catch (err: any) {
    dispatch({
      type: CampaignActionTypes.DELETE_CAMPAIGN_ERROR,
      payload: err.message || "Unknown error",
    });
  }
};

/** ─── SCHEDULE ───────────────────────────────────────────────── **/
export const campaignScheduleRequest = (
  id: string,
  scheduleDto: { immediate: boolean; scheduled_time_utc?: string | null; send_in_local_time: boolean; recurrence_rule?: any }
): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: CampaignActionTypes.UPDATE_CAMPAIGN_REQUEST }); // reuse “update” loading
  try {
    const res = await fetch(
      `${BASE}/api/v1/campaigns/${id}/schedule`,
      {
        method: "POST",
        headers: makeHeaders(token),
        body: JSON.stringify(scheduleDto),
      }
    );
    if (!res.ok) {
      const body = await res.json();
      throw new Error(body.message || res.statusText);
    }
    dispatch({ type: CampaignActionTypes.UPDATE_CAMPAIGN_SUCCESS, payload: { id, status: "SCHEDULED" } as any });
  } catch (err: any) {
    dispatch({ type: CampaignActionTypes.UPDATE_CAMPAIGN_ERROR, payload: err.message || "Unknown error" });
  }
};

/** ─── GET RECIPIENTS ───────────────────────────────────────────── */
// actions.ts

interface RecipFetchParams {
  campaignId: string;
  page?: number;
  size?: number;
  search?: string;
  status?: string;
  sent?: string;    // "true"|"false"|""
  opened?: string;
  clicked?: string;
}

// Updated action
export const campaignRecipientsRequest = ({
  campaignId,
  page = 0,
  size = 20,
  search = "",
  status = "",
  sent = "",
  opened = "",
  clicked = "",
}: RecipFetchParams): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: CampaignActionTypes.GET_CAMPAIGN_RECIPIENTS_REQUEST });

  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("size", String(size));
  if (search) params.append("search", search);
  if (status) params.append("status", status);
  if (sent) params.append("sent", sent);
  if (opened) params.append("opened", opened);
  if (clicked) params.append("clicked", clicked);

  try {
    const res  = await fetch(
      `${BASE}/api/v1/recipients/by-campaign/${campaignId}?${params}`,
      { headers: makeHeaders(token) }
    );
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || res.statusText);

    const { content, total_elements, page: pg, size: sz, total_pages, first, last } = body as {
      content: Recipient[];
      total_elements: number;
      page: number; size: number; total_pages: number; first: boolean; last: boolean;
    };
    const pagination: Pagination = {
      page: pg, size: sz, pages: total_pages,
      elements: total_elements, first, last
    };

    dispatch({
      type: CampaignActionTypes.GET_CAMPAIGN_RECIPIENTS_SUCCESS,
      payload: { content, pagination },
    });
  } catch (err: any) {
    dispatch({
      type: CampaignActionTypes.GET_CAMPAIGN_RECIPIENTS_ERROR,
      payload: err.message || "Unknown error",
    });
  }
};


/** ─── FULL REPORT ──────────────────────────────────────────────── */
export const campaignFullReportRequest = (campaignId: string): AppThunk =>
  async (dispatch, getState) => {
    const { auth: { token } } = getState();
    dispatch({ type: CampaignActionTypes.GET_CAMPAIGN_REPORT_REQUEST });
    try {
      const res  = await fetch(
        `${BASE}/api/v1/campaigns/full-report/${campaignId}`,
        { headers: makeHeaders(token) }
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || res.statusText);
      dispatch({
        type: CampaignActionTypes.GET_CAMPAIGN_REPORT_SUCCESS,
        payload: body as CampaignReportDto,
      });
    } catch (err: any) {
      dispatch({
        type: CampaignActionTypes.GET_CAMPAIGN_REPORT_ERROR,
        payload: err.message || "Unknown error",
      });
    }
  };

/** ─── EVENTS ───────────────────────────────────────────────────── */
interface EventsFetchParams { campaignId: string; page?: number; size?: number; }
export const campaignEventsRequest = ({
  campaignId,
  page = 0,
  size = 20
}: EventsFetchParams): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: CampaignActionTypes.GET_CAMPAIGN_EVENTS_REQUEST });
  const qs = new URLSearchParams({ page: String(page), size: String(size) });
  try {
    const res  = await fetch(
      `${BASE}/api/v1/events/by-campaign/${campaignId}?${qs}`,
      { headers: makeHeaders(token) }
    );
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || res.statusText);

    const { content, page: pg, size: sz, totalPages, totalElements, first, last } = body;
    dispatch({
      type: CampaignActionTypes.GET_CAMPAIGN_EVENTS_SUCCESS,
      payload: {
        content: content as Event[],
        pagination: { page: pg, size: sz, pages: totalPages, elements: totalElements, first, last }
      }
    });
  } catch (err: any) {
    dispatch({
      type: CampaignActionTypes.GET_CAMPAIGN_EVENTS_ERROR,
      payload: err.message || "Unknown error"
    });
  }
};
